import { useSupabase } from '@/hooks/useSupabase'
import { supabase } from '@/lib/supabase'
import type { Concept, Connection } from '@/types'

export function useConcepts(limit = 50) {
  const dependencies = [limit]
  
  return useSupabase<Concept[]>(async () => {
    const { data, error } = await supabase
      .from('concepts')
      .select(`
        *,
        domain:domains(*)
      `)
      .order('synapse_strength', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }, dependencies)
}

export function useConceptById(id: string | undefined) {
  const dependencies = [id]
  
  return useSupabase<Concept | null>(async () => {
    if (!id) return null
    
    const { data, error } = await supabase
      .from('concepts')
      .select(`
        *,
        domain:domains(*)
      `)
      .eq('id', id)
      .maybeSingle()

    // maybeSingle returns null if not found, only throw on real errors
    if (error && error.code !== 'PGRST116') throw error
    return data
  }, dependencies, !!id)
}

export function useConceptSearch(query: string, limit = 20) {
  const dependencies = [query, limit]
  
  return useSupabase<Concept[]>(async () => {
    if (!query.trim()) return []
    
    const { data, error } = await supabase
      .from('concepts')
      .select(`
        *,
        domain:domains(*)
      `)
      .or(`name.ilike.%${query.replace(/[%_(),]/g, '')}%,definition.ilike.%${query.replace(/[%_(),]/g, '')}%`)
      .order('synapse_strength', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }, dependencies, !!query.trim())
}

export function useTopConcepts(limit = 10) {
  const dependencies = [limit]
  
  return useSupabase<Concept[]>(async () => {
    const { data, error } = await supabase
      .from('concepts')
      .select(`
        *,
        domain:domains(*)
      `)
      .order('access_count', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }, dependencies)
}

export function useConceptsByDomain(domainId: string | undefined, limit = 30) {
  const dependencies = [domainId, limit]
  
  return useSupabase<Concept[]>(async () => {
    if (!domainId) return []
    
    const { data, error } = await supabase
      .from('concepts')
      .select(`
        *,
        domain:domains(*)
      `)
      .eq('domain_id', domainId)
      .order('synapse_strength', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }, dependencies, !!domainId)
}

// Hook for concept connections
export function useConceptConnections(conceptId: string | undefined) {
  const dependencies = [conceptId]
  
  return useSupabase<Connection[]>(async () => {
    if (!conceptId) return []

    // Get connections without FK joins
    const { data: connections, error } = await supabase
      .from('connections')
      .select('*')
      .or(`from_node_id.eq.${conceptId},to_node_id.eq.${conceptId}`)
      .eq('from_node_type', 'concept')
      .eq('to_node_type', 'concept')

    if (error) throw error
    if (!connections || connections.length === 0) return []

    // Get related concept IDs
    const relatedIds = connections
      .flatMap(c => [c.from_node_id, c.to_node_id])
      .filter(id => id !== conceptId)
      .filter((id, idx, arr) => arr.indexOf(id) === idx)

    // Fetch concept details
    const conceptMap = new Map<string, Concept>()
    if (relatedIds.length > 0) {
      const { data: concepts } = await supabase
        .from('concepts')
        .select('*')
        .in('id', relatedIds)

      concepts?.forEach((c: Concept) => conceptMap.set(c.id, c))
    }

    // Merge connection data with concept details
    return connections
      .map(conn => ({
        ...conn,
        from_concept: conceptMap.get(conn.from_node_id),
        to_concept: conceptMap.get(conn.to_node_id)
      }))
      .sort((a, b) => b.weight - a.weight)
  }, dependencies, !!conceptId)
}

// Hook for creating a new concept (manual input)
export function useCreateConcept() {
  const createConcept = async (concept: {
    name: string
    definition: string
    domain_id?: string
    complexity_level: number
  }) => {
    const { data, error } = await supabase
      .from('concepts')
      .insert({
        ...concept,
        mesh_eligible: true,
        access_count: 0,
        synapse_strength: 0.5, // Initial strength
        created_at: new Date().toISOString()
      })
      .select()
      .single()

    if (error) throw error
    return data
  }

  return { createConcept }
}

// Hook for updating concept access
export function useConceptAccess() {
  const recordAccess = async (conceptId: string) => {
    const { error } = await supabase.rpc('increment_concept_access', {
      concept_id: conceptId
    })
    
    if (error) throw error
  }

  return { recordAccess }
}